import { z } from '../../../zod';
import { MESSAGE_VARIABLES_HELP, MESSAGE_VARIABLES_OUTPUT_HELP } from '../messageVariables';

export const NotificationFieldsInputValidationSchema = z.object({
  sendPaymentNotification: z.boolean().nullish().meta({
    description: 'Send payment/submission notification emails to the store owner.',
    example: true,
  }),
  notifyEmail: z.string().nullish().meta({
    description: 'Email addresses to send notifications to. Supports comma separated emails.',
    example: 'owner@example.com,owner2@example.com',
  }),
  sendEmailConfirmation: z.boolean().nullish().meta({
    description: 'Send an email confirmation to the customer. Defaults to `true`.',
    example: true,
  }),
  customizeEmailConfirmation: z.boolean().nullish().meta({
    description:
      'Use a custom email confirmation template. If enabled, provide `confirmationEmailSubject` and `confirmationEmailMessage`.',
    example: true,
  }),
  confirmationEmailSubject: z.string().nullish().meta({
    description: 'Custom confirmation email subject.',
    example: 'Thank you!',
  }),
  confirmationEmailMessage: z
    .string()
    .nullish()
    .meta({
      description: `Custom confirmation email message. Accepts HTML or plain text. ${MESSAGE_VARIABLES_HELP}`,
      example: '<p>Email content</p>',
    }),
  confirmationEmailShowLogo: z.boolean().nullish().meta({
    description: 'Show store logo in confirmation email.',
    example: true,
  }),
  confirmationEmailShowStoreName: z.boolean().nullish().meta({
    description: 'Show store name in confirmation email.',
    example: true,
  }),
  googleIndex: z.boolean().nullish().meta({
    description: 'Allow Google to index this page for search results. Defaults to `true`.',
    example: true,
  }),
  trackingCodes: z
    .string()
    .nullish()
    .meta({
      description: 'Custom tracking scripts to include on the page. Supports HTML `<script>` tags.',
      example: `
    <!-- Google Tag Manager -->
<script>(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com;
})(window,document,'script','dataLayer','GTM-XXXXXX');</script>
<!-- End Google Tag Manager -->
`,
    }),
});

export const NotificationFieldsResponseValidationSchema = z.object({
  sendPaymentNotification: z.boolean().nullable().optional().meta({
    description: 'Send notification emails to the store owner.',
  }),
  notifyEmail: z.string().nullish().meta({
    description: 'Email addresses to send notifications to.',
  }),
  sendEmailConfirmation: z.boolean().nullable().optional().meta({
    description: 'Send an email confirmation to the customer.',
  }),
  customizeEmailConfirmation: z.boolean().nullable().optional().meta({
    description: 'Use a custom email confirmation template.',
  }),
  confirmationEmailSubject: z.string().nullish().meta({
    description: 'Custom confirmation email subject.',
  }),
  confirmationEmailMessage: z
    .string()
    .nullish()
    .meta({
      description: `Custom confirmation email message. ${MESSAGE_VARIABLES_OUTPUT_HELP}`,
    }),
  confirmationEmailShowLogo: z.boolean().nullable().optional().meta({
    description: 'Show store logo in confirmation email.',
  }),
  confirmationEmailShowStoreName: z.boolean().nullable().optional().meta({
    description: 'Show store name in confirmation email.',
  }),
  googleIndex: z.boolean().nullable().optional().meta({
    description: 'Allow Google to index this page.',
  }),
  trackingCodes: z.string().nullish().meta({
    description: 'Custom tracking scripts for the page.',
  }),
});

type CustomEmailConfirmationFields = {
  customizeEmailConfirmation?: boolean | null;
  confirmationEmailSubject?: string | null;
  confirmationEmailMessage?: string | null;
};

export function withCustomEmailConfirmationValidation<
  T extends z.ZodType<CustomEmailConfirmationFields>,
>(schema: T): T {
  return schema.refine(
    data => {
      const customizeEmailConfirmation = data.customizeEmailConfirmation === true;
      const hasSubject = !!data.confirmationEmailSubject;
      const hasMessage = !!data.confirmationEmailMessage;

      if (customizeEmailConfirmation) {
        return hasSubject && hasMessage;
      }

      return !hasSubject && !hasMessage;
    },
    {
      message:
        'Must pass `confirmationEmailSubject` and `confirmationEmailMessage` when `customizeEmailConfirmation`.',
      path: ['customizeEmailConfirmation'],
    },
  ) as T;
}
